/* @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { useEffect, useState } from 'react';
import * as React from 'react';

import { getLabelForAITranslation } from 'js/lib/languageLocalized';

import { Grid } from '@coursera/cds-core';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import { getSubtitleLabel } from 'bundles/common/utils/subtitleUtils';
import ArrowSvg from 'bundles/video-player/assets/svg/ArrowSvg';
import CrosSvg from 'bundles/video-player/assets/svg/CrosSvg';
import HeadsetSmallSvg from 'bundles/video-player/assets/svg/HeadsetSmall';
import { VIDEO_PLAYER_AUDIO_LANGUAGE_NOT_SET, VIDEO_PLAYER_SUBTITLE_NOT_SET } from 'bundles/video-player/constants';
import type { VideoPlayerEventingV3UIFunction } from 'bundles/video-player/types/VideoPlayer';
import userPreferences from 'bundles/video-player/utils/userPreferences';

import _t from 'i18n!nls/video-player';

const styles = {
  nudgeContainer: css`
    position: absolute;
    top: var(--cds-spacing-100);
    right: var(--cds-spacing-100);
    width: 100%;
    max-width: 375px;
    background-color: rgb(0 0 0 / 66%);
    padding: var(--cds-spacing-200);
    padding-bottom: var(--cds-spacing-300);
    border-radius: var(--cds-spacing-100);
    box-shadow: 0 2px 8px 0 rgb(0 0 0 / 32%);
    backdrop-filter: blur(23px);
    color: white;
  `,
  nudgeTitle: css`
    font-size: 15px;
    font-weight: 600;
  `,
  nudgeClose: css`
    cursor: pointer;
  `,
  nudgeDescription: css`
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    margin-top: var(--cds-spacing-50);
  `,
  nudgeFooter: css`
    font-size: 14px;
    font-weight: 600;
    margin-top: var(--cds-spacing-200);
    cursor: pointer;
  `,
  nudgeArrow: css`
    padding-left: 10px;
  `,
};

type Props = {
  visible: boolean;
  languageCode: string | null;
  playerHasLoaded: boolean;
  trackVideoPlayerClickEvent: VideoPlayerEventingV3UIFunction;
  trackVideoPlayerViewEvent: VideoPlayerEventingV3UIFunction;
  onDubbedAudioChoiceClick: (option: { id: string; languageKey: string; label: string }) => void;
};

const NUDGE_KEY = 'user-education-nudge-displayed';

const AudioDubbingNudgeTooltip = ({
  visible = false,
  languageCode,
  playerHasLoaded,
  trackVideoPlayerClickEvent,
  trackVideoPlayerViewEvent,
  onDubbedAudioChoiceClick,
}: Props) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasFiredInitialViewEvent, setHasFiredInitialViewEvent] = useState(false);

  const hasNudgeBeenDisplayed = localStorageEx.getItem(NUDGE_KEY, JSON.parse, false);
  const hideAudioDubbingNudgeTooltip = !visible || isDismissed || hasNudgeBeenDisplayed;

  useEffect(() => {
    if (playerHasLoaded && !hasFiredInitialViewEvent && !hideAudioDubbingNudgeTooltip) {
      trackVideoPlayerViewEvent('video_audio_warning_tooltip', {});
      setHasFiredInitialViewEvent(true);
    }
  }, [hasFiredInitialViewEvent, hideAudioDubbingNudgeTooltip, playerHasLoaded, trackVideoPlayerViewEvent]);

  const languageString = languageCode ? getSubtitleLabel(languageCode, false) : '';

  const dismissNudge = () => {
    setIsDismissed(true);
    localStorageEx.setItem(NUDGE_KEY, true, JSON.stringify);
  };

  const dissmissNudgeAndLogEvent = () => {
    trackVideoPlayerClickEvent('video_audio_warning_tooltip_dismiss', {});
    dismissNudge();
  };

  const switchAudioLanguage = () => {
    if (!languageCode) return;
    const option = {
      id: getLabelForAITranslation(languageCode),
      label: getLabelForAITranslation(languageCode),
      languageKey: languageCode,
    };

    onDubbedAudioChoiceClick(option);

    const audioLanguageVideoMetadata = {
      audioLanguage: languageCode,
      audioLanguageEnabled: true,
      previousAudioLanguage: 'none',
    };

    if (userPreferences.get('subtitleLanguage') === VIDEO_PLAYER_AUDIO_LANGUAGE_NOT_SET) {
      trackVideoPlayerClickEvent('video_audio_warning_tooltip_switch', {
        ...audioLanguageVideoMetadata,
      });
    } else {
      trackVideoPlayerClickEvent('video_settings_menu_audio_language_option', {
        ...audioLanguageVideoMetadata,
        previousSubtitleLanguage: userPreferences.get('subtitleLanguage'),
        subtitleLanguage: option.languageKey ?? VIDEO_PLAYER_SUBTITLE_NOT_SET,
      });
    }
    dismissNudge();
  };

  if (hideAudioDubbingNudgeTooltip) {
    return null;
  }

  return (
    <Grid container css={styles.nudgeContainer} onClick={(e) => e.stopPropagation()} data-testid="user-education-nudge">
      <Grid item container xs={12}>
        <Grid item xs={1}>
          <HeadsetSmallSvg />
        </Grid>
        <Grid item xs={10}>
          <div css={styles.nudgeTitle}>{_t('AI-dubbing available')}</div>
        </Grid>
        <Grid item xs={1} css={styles.nudgeClose} onClick={dissmissNudgeAndLogEvent} data-testid="nudge-close">
          <CrosSvg />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <div css={styles.nudgeDescription}>
          {_t('Switch your audio settings to #{languageString} for a better learning experience.', { languageString })}
        </div>
      </Grid>
      <Grid item container xs={12} css={styles.nudgeFooter} onClick={switchAudioLanguage} data-testid="nudge-footer">
        <Grid item xs={6}>
          <div>{_t('Switch audio to #{languageString}', { languageString })}</div>
        </Grid>
        <Grid item xs={6} css={styles.nudgeArrow}>
          <ArrowSvg />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AudioDubbingNudgeTooltip;
